import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import AuthGuard from "../components/auth-guard";
import { useIsMobile } from "../hooks/use-mobile";
import { AuthService } from "../lib/auth";
import { queryClient } from "../lib/queryClient";
import { isExperimentDirectEntryUser, startExperimentDirectInterview } from "../lib/experiment-direct-entry";
import { GuestDirectBriefingGate } from "../components/GuestDirectBriefingGate";
import type { AuthStatus, UserProfile } from "../types";
import { STUDENT_HEADER_HEIGHT } from "../components/student/StudentHeader";

// ===== Design tokens =====
const COLORS = {
  bg: "#FAFAF7",
  surface: "#FFFFFF",
  ink: "#18181B",
  muted: "#71717A",
  line: "#E4E4E7",
};

const SCENARIO_CARD_CATEGORY_LIMIT = 2;

// ===== Difficulty = scenario type =====
// 易 = 基本聴取 (intake) / 中 = 行動変容なし / 難 = 行動変容あり
type Difficulty = "易" | "中" | "難";

const DIFFICULTY_META: Record<
  Difficulty,
  { label: string; sub: string; color: string; soft: string; dots: string }
> = {
  易: { label: "やさしい", sub: "基本聴取", color: "#0E7490", soft: "#ECFEFF", dots: "●○○" },
  中: { label: "ふつう", sub: "行動変容なし", color: "#7C3AED", soft: "#F5F3FF", dots: "●●○" },
  難: { label: "むずかしい", sub: "行動変容あり", color: "#BE123C", soft: "#FFF1F2", dots: "●●●" },
};

// カテゴリ名はサーバーが条件データから付ける targetCategoryLabels を使う。
// 番号の意味は CSV の版で変わる（旧C6=喫煙・新C6=マウスウォッシュ）ため、名前がなければ番号のまま出す。
function themeLabel(catId: string, labels?: Record<string, string>): string {
  return labels?.[catId] ?? catId;
}

function buildCategoryOptionsFromScenarios(
  scenarios: Scenario[],
): Array<{ id: string; theme: string }> {
  const map = new Map<string, string>();
  for (const scenario of scenarios) {
    for (const id of scenario.targetCategories) {
      const label = themeLabel(id, scenario.targetCategoryLabels);
      if (!map.has(id)) map.set(id, label);
    }
  }
  return Array.from(map.entries())
    .map(([id, theme]) => ({ id, theme }))
    .sort((a, b) => {
      const na = Number.parseInt(a.id.replace(/^C/, ""), 10);
      const nb = Number.parseInt(b.id.replace(/^C/, ""), 10);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      return a.id.localeCompare(b.id);
    });
}

function ageRangeLabel(ageRange: string): string {
  if (ageRange === "20-34") return "20〜34歳";
  if (ageRange === "35-49") return "35〜49歳";
  if (ageRange === "50-64") return "50〜64歳";
  if (ageRange === "65+") return "65歳以上";
  if (ageRange === "50+") return "50歳以上";
  return ageRange;
}

// ===== API types =====
interface Scenario {
  id: number;
  scenarioKey?: string;
  guidanceLevel?: "detailed" | "viewpoints" | "purpose_only" | null;
  guidanceLabel?: string | null;
  guidanceOptions?: Array<{
    level: "detailed" | "viewpoints" | "purpose_only";
    label: string;
    taskCount: number;
  }>;
  conditionId: number;
  mode: string;
  hasBehaviorChange: boolean;
  gender: string;
  ageRange: string;
  targetCategories: string[];
  targetCategoryLabels?: Record<string, string>;
  tasks: Array<{ no: number; title: string; details?: string[] }>;
  questionnaire: string | null;
  instruction: Record<string, unknown> | null;
  summary?: string | null;
}

function difficultyOf(s: Scenario): Difficulty {
  if (s.mode === "intake") return "易";
  return s.hasBehaviorChange ? "難" : "中";
}

function summaryOf(s: Scenario): string {
  if (s.summary && s.summary.trim()) return s.summary;
  // フォールバック: 最初のタスクからネタバレなしの簡易要約を合成
  const firstTask = s.tasks[0]?.title;
  if (firstTask) return firstTask;
  return "シナリオの概要は未設定です。";
}

function visibleScenarioCategories(s: Scenario): string[] {
  return s.targetCategories.slice(0, SCENARIO_CARD_CATEGORY_LIMIT);
}

function remainingScenarioCategoryCount(s: Scenario): number {
  return Math.max(0, s.targetCategories.length - SCENARIO_CARD_CATEGORY_LIMIT);
}

export default function SelectFilter() {
  return (
    <AuthGuard>
      <GuestDirectBriefingGate>
        <SelectFilterContent />
      </GuestDirectBriefingGate>
    </AuthGuard>
  );
}

function SelectFilterContent() {
  const [, setLocation] = useLocation();
  const authService = AuthService.getInstance();
  const [difficulties, setDifficulties] = useState<Record<Difficulty, boolean>>({
    易: true,
    中: true,
    難: true,
  });
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [themeFilterOpen, setThemeFilterOpen] = useState(false);
  const isMobile = useIsMobile();

  const { data: authStatus, isLoading: authLoading } = useQuery<AuthStatus>({
    queryKey: ["authStatus"],
    queryFn: () => authService.getAuthStatus({ forceRefresh: true }),
    staleTime: 0,
  });

  const { data: currentUser, isLoading: userLoading } = useQuery<UserProfile | null>({
    queryKey: ["currentUser"],
    queryFn: () => authService.getCurrentUser(),
    enabled: authStatus?.isAuthenticated === true,
    staleTime: 0,
  });

  const directEntryUser = currentUser ?? authStatus?.user;
  const shouldUseDirectEntry =
    authStatus?.experimentEntryFlow === "ab_direct_interview" && directEntryUser?.provider === "local";

  const { data, isLoading } = useQuery<{ scenarios: Scenario[] }>({
    queryKey: ["/api/scenarios"],
    enabled: !shouldUseDirectEntry,
    staleTime: 30000,
  });

  useEffect(() => {
    if (authLoading || userLoading) return;
    if (!shouldUseDirectEntry) return;
    if (!authStatus.isAuthenticated) {
      setLocation("/login");
      return;
    }

    const entryTarget = directEntryUser?.experimentEntryTarget;
    if (isExperimentDirectEntryUser(directEntryUser) && entryTarget) {
      startExperimentDirectInterview(entryTarget)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["/api/avatar/current"] });
          setLocation("/interview");
        })
        .catch((error) => {
          console.error("実証実験の直接面接開始に失敗しました:", error);
          setLocation("/interview");
        });
      return;
    }

    setLocation("/login");
  }, [authLoading, authStatus, currentUser, directEntryUser, setLocation, shouldUseDirectEntry, userLoading]);

  if (shouldUseDirectEntry) {
    return (
      <div className="fixed inset-0 top-[68px] flex items-center justify-center" style={{ background: COLORS.bg }}>
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: COLORS.ink }} />
        <span className="ml-3" style={{ color: COLORS.muted, fontWeight: 700 }}>
          面接画面へ移動しています...
        </span>
      </div>
    );
  }

  const scenarios = data?.scenarios ?? [];
  const categoryOptions = buildCategoryOptionsFromScenarios(scenarios);

  const filtered = scenarios.filter((s) => {
    if (!difficulties[difficultyOf(s)]) return false;
    if (selectedCategoryIds.length === 0) return true;
    return s.targetCategories.some((c) => selectedCategoryIds.includes(c));
  });

  const goToPreview = (scenario: Scenario) => {
    const guidance = scenario.guidanceLevel
      ? `&guidanceLevel=${encodeURIComponent(scenario.guidanceLevel)}`
      : "";
    setLocation(`/scenario-preview?instructionId=${scenario.id}${guidance}`);
  };

  const rollRandom = () => {
    if (filtered.length === 0) return;
    const pick = filtered[Math.floor(Math.random() * filtered.length)];
    goToPreview(pick);
  };

  const toggleDiff = (d: Difficulty) =>
    setDifficulties((p) => ({ ...p, [d]: !p[d] }));

  const toggleCategory = (id: string | null) => {
    if (id === null) {
      setSelectedCategoryIds([]);
      return;
    }
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  if (isMobile) {
    return (
      <div
        className="fixed inset-0 flex flex-col overflow-hidden"
        style={{
          top: STUDENT_HEADER_HEIGHT,
          background: COLORS.bg,
          color: COLORS.ink,
          fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
        }}
      >
        <div
          style={{
            flex: "0 0 auto",
            padding: "18px 20px 16px",
            background: COLORS.surface,
            borderBottom: `1px solid ${COLORS.line}`,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: COLORS.muted,
              letterSpacing: "0.15em",
              marginBottom: 8,
            }}
          >
            STEP 01
          </div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: -0.4,
              lineHeight: 1.25,
            }}
          >
            今日はどの練習にする？
          </div>
        </div>

        <div
          style={{
            flex: "0 0 auto",
            padding: "16px 20px",
            background: COLORS.surface,
            borderBottom: `1px solid ${COLORS.line}`,
          }}
        >
          <FilterLabel>難易度</FilterLabel>
          <div style={{ marginTop: 10 }}>
            <MobileDifficultyFilter difficulties={difficulties} onToggle={toggleDiff} />
          </div>
        </div>

        <div
          style={{
            flex: "0 0 auto",
            padding: "14px 20px",
            background: COLORS.surface,
            borderBottom: `1px solid ${COLORS.line}`,
          }}
        >
          <button
            type="button"
            onClick={() => setThemeFilterOpen((open) => !open)}
            style={{
              width: "100%",
              border: "none",
              background: "transparent",
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              fontFamily: "inherit",
              cursor: "pointer",
              color: COLORS.ink,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: -0.2 }}>
              テーマで絞り込み
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <span style={{ fontSize: 12.5, color: COLORS.muted }}>
                {themeFilterOpen
                  ? "閉じる"
                  : selectedCategoryIds.length === 0
                    ? "すべて表示"
                    : `${selectedCategoryIds.length}件選択中`}
              </span>
              <ChevronDown
                size={18}
                style={{
                  color: COLORS.ink,
                  transform: themeFilterOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 140ms ease",
                }}
              />
            </div>
          </button>
          {themeFilterOpen && (
            <div style={{ marginTop: 12 }}>
              <MobileThemeChips
                options={categoryOptions}
                selected={selectedCategoryIds}
                onToggle={toggleCategory}
              />
            </div>
          )}
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <div style={{ padding: "14px 20px 104px" }}>
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: COLORS.muted }} />
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: "center", padding: "52px 0", color: COLORS.muted, fontSize: 13 }}>
                条件に一致するシナリオがありません
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {filtered.map((s) => (
                  <MobileScenarioCard key={s.scenarioKey ?? String(s.id)} s={s} onStart={() => goToPreview(s)} />
                ))}
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            right: 20,
            bottom: "max(18px, env(safe-area-inset-bottom))",
          }}
        >
          <button
            type="button"
            onClick={rollRandom}
            disabled={filtered.length === 0}
            style={{
              minHeight: 64,
              padding: "0 18px",
              borderRadius: 999,
              background:
                filtered.length === 0
                  ? "linear-gradient(135deg, #D4D4D8 0%, #A1A1AA 100%)"
                  : "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)",
              color: "#FFFFFF",
              border: "none",
              boxShadow:
                filtered.length === 0
                  ? "0 2px 6px rgba(0,0,0,0.12)"
                  : "0 0 0 4px rgba(255,138,61,0.15), 0 10px 28px rgba(255,107,26,0.42), 0 2px 6px rgba(0,0,0,0.12)",
              cursor: filtered.length === 0 ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              fontSize: 14,
              fontWeight: 800,
              letterSpacing: 0.3,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              maxWidth: "calc(100vw - 40px)",
            }}
          >
            <DiceIcon />
            <span>この中からランダムで開始</span>
            <span aria-hidden>→</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 top-[68px] flex flex-col overflow-hidden"
      style={{
        background: COLORS.bg,
        color: COLORS.ink,
        fontFamily: '"Hiragino Sans", "Noto Sans JP", system-ui, sans-serif',
      }}
    >
      {/* Header block */}
      <div
        style={{
          padding: "22px 32px 18px",
          background: COLORS.surface,
          borderBottom: `1px solid ${COLORS.line}`,
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: COLORS.muted,
            letterSpacing: 1.5,
            marginBottom: 6,
          }}
        >
          STEP 01
        </div>
        <div
          style={{
            fontSize: 32,
            fontWeight: 800,
            letterSpacing: -0.5,
            lineHeight: 1.2,
          }}
        >
          今日はどの練習にする？
        </div>
      </div>

      {/* Difficulty filter bar */}
      <div
        style={{
          padding: "14px 32px",
          background: COLORS.surface,
          borderBottom: `1px solid ${COLORS.line}`,
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <FilterLabel>難易度</FilterLabel>
        <DifficultyFilter difficulties={difficulties} onToggle={toggleDiff} />
      </div>

      {/* Theme filter: collapsed by default so scenarios remain the primary content. */}
      <div
        style={{
          padding: "12px 32px",
          background: COLORS.surface,
          borderBottom: `1px solid ${COLORS.line}`,
        }}
      >
        <button
          type="button"
          onClick={() => setThemeFilterOpen((open) => !open)}
          aria-expanded={themeFilterOpen}
          style={{
            width: "100%",
            border: "none",
            background: "transparent",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            color: COLORS.ink,
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "left",
          }}
        >
          <span style={{ fontSize: 15, fontWeight: 800 }}>テーマで絞り込み</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 14, color: COLORS.muted }}>
              {themeFilterOpen
                ? "閉じる"
                : selectedCategoryIds.length === 0
                  ? "すべて表示"
                  : `${selectedCategoryIds.length}件選択中`}
            </span>
            <ChevronDown
              size={18}
              aria-hidden
              style={{
                transform: themeFilterOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 140ms ease",
              }}
            />
          </div>
        </button>
        {themeFilterOpen && (
          <div style={{ marginTop: 12 }}>
            <ThemeChips
              options={categoryOptions}
              selected={selectedCategoryIds}
              onToggle={toggleCategory}
            />
          </div>
        )}
      </div>

      {/* 件数 + ランダム開始バー */}
      <div
        style={{
          padding: "14px 32px",
          background: COLORS.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14,
        }}
      >
        <span
          style={{
            fontSize: 16,
            color: COLORS.ink,
            fontWeight: 600,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          条件に合うシナリオ {filtered.length}件
        </span>
        <button
          type="button"
          onClick={rollRandom}
          disabled={filtered.length === 0}
          title="ランダムで開始"
          style={{
            padding: "12px 20px",
            borderRadius: 999,
            background:
              filtered.length === 0
                ? "linear-gradient(135deg, #D4D4D8 0%, #A1A1AA 100%)"
                : "linear-gradient(135deg, #FF8A3D 0%, #FF6B1A 100%)",
            color: "#fff",
            border: "none",
            boxShadow:
              filtered.length === 0
                ? "0 2px 6px rgba(0,0,0,0.12)"
                : "0 0 0 4px rgba(255,138,61,0.15), 0 10px 28px rgba(255,107,26,0.45), 0 2px 6px rgba(0,0,0,0.12)",
            cursor: filtered.length === 0 ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontFamily: "inherit",
            fontSize: 16,
            fontWeight: 800,
            letterSpacing: 0.3,
            transition: "transform 150ms ease-out, box-shadow 150ms ease-out",
          }}
          onMouseEnter={(e) => {
            if (filtered.length === 0) return;
            e.currentTarget.style.transform = "scale(1.03)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <DiceIcon />
          この中からランダムで開始
        </button>
      </div>

      {/* Card grid (scroll region) */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ padding: "8px 32px 40px" }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2
                className="w-8 h-8 animate-spin"
                style={{ color: COLORS.muted }}
              />
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: 60,
                color: COLORS.muted,
              }}
            >
              条件に一致するシナリオがありません
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 14,
              }}
            >
              {filtered.map((s) => (
                <ScenarioCard key={s.scenarioKey ?? String(s.id)} s={s} onStart={() => goToPreview(s)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== Sub-components =====

function FilterLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 14,
        fontWeight: 800,
        color: COLORS.muted,
        letterSpacing: 1.2,
        flexShrink: 0,
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

function DifficultyFilter({
  difficulties,
  onToggle,
}: {
  difficulties: Record<Difficulty, boolean>;
  onToggle: (d: Difficulty) => void;
}) {
  const items: Difficulty[] = ["易", "中", "難"];
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {items.map((d) => {
        const meta = DIFFICULTY_META[d];
        const on = difficulties[d];
        return (
          <button
            key={d}
            type="button"
            onClick={() => onToggle(d)}
            style={{
              padding: "8px 14px",
              borderRadius: 999,
              border: `1px solid ${on ? meta.color : COLORS.line}`,
              background: on ? meta.soft : COLORS.surface,
              color: on ? meta.color : COLORS.muted,
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              letterSpacing: 0.2,
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                fontFamily: "system-ui",
                fontSize: 11,
                letterSpacing: 1.2,
                opacity: on ? 1 : 0.45,
              }}
            >
              {meta.dots}
            </span>
            <span>
              {d} · {meta.sub}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MobileDifficultyFilter({
  difficulties,
  onToggle,
}: {
  difficulties: Record<Difficulty, boolean>;
  onToggle: (d: Difficulty) => void;
}) {
  const items: Difficulty[] = ["易", "中", "難"];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
      {items.map((d) => {
        const meta = DIFFICULTY_META[d];
        const on = difficulties[d];
        return (
          <button
            key={d}
            type="button"
            onClick={() => onToggle(d)}
            style={{
              minHeight: 44,
              borderRadius: 10,
              border: `1px solid ${on ? meta.color : COLORS.line}`,
              background: on ? meta.soft : COLORS.surface,
              color: on ? meta.color : COLORS.muted,
              fontSize: 13,
              fontWeight: 800,
              cursor: "pointer",
              fontFamily: "inherit",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              whiteSpace: "nowrap",
            }}
          >
            <span>{d}</span>
            <span style={{ fontFamily: "system-ui", fontSize: 10, letterSpacing: 1.5 }}>
              {meta.dots}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function ThemeChips({
  options,
  selected,
  onToggle,
}: {
  options: Array<{ id: string; theme: string }>;
  selected: string[];
  onToggle: (id: string | null) => void;
}) {
  const allOn = selected.length === 0;
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <button
        type="button"
        onClick={() => onToggle(null)}
        style={{
          padding: "7px 13px",
          borderRadius: 999,
          border: `1px solid ${allOn ? COLORS.ink : COLORS.line}`,
          background: allOn ? COLORS.ink : COLORS.surface,
          color: allOn ? "#FFFFFF" : COLORS.muted,
          fontSize: 14,
          fontWeight: 700,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        すべて
      </button>
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onToggle(o.id)}
            style={{
              padding: "7px 13px",
              borderRadius: 999,
              border: `1px solid ${on ? COLORS.ink : COLORS.line}`,
              background: on ? "#F4F4F5" : COLORS.surface,
              color: on ? COLORS.ink : COLORS.muted,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
              whiteSpace: "nowrap",
            }}
          >
            {o.theme}
          </button>
        );
      })}
    </div>
  );
}

function MobileThemeChips({
  options,
  selected,
  onToggle,
}: {
  options: Array<{ id: string; theme: string }>;
  selected: string[];
  onToggle: (id: string | null) => void;
}) {
  const allOn = selected.length === 0;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      <button
        type="button"
        onClick={() => onToggle(null)}
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          border: `1px solid ${allOn ? COLORS.ink : COLORS.line}`,
          background: allOn ? COLORS.ink : COLORS.surface,
          color: allOn ? "#FFFFFF" : COLORS.muted,
          fontSize: 12,
          fontWeight: 800,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        すべて
      </button>
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onToggle(o.id)}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: `1px solid ${on ? COLORS.ink : COLORS.line}`,
              background: on ? "#F4F4F5" : COLORS.surface,
              color: on ? COLORS.ink : COLORS.muted,
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              maxWidth: "100%",
            }}
          >
            <span
              style={{
                display: "block",
                maxWidth: 156,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {shortThemeLabel(o.theme)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function shortThemeLabel(label: string): string {
  return label
    .replace("・現在直面している症状・問題", "")
    .replace("（ブラッシング・清掃用具）", "")
    .replace("（子供、高齢者、障がい者等）", "")
    .replace("現在の状況", "")
    .trim();
}

function ScenarioCard({
  s,
  onStart,
}: {
  s: Scenario;
  onStart: () => void;
}) {
  const d = difficultyOf(s);
  const meta = DIFFICULTY_META[d];

  return (
    <div
      style={{
        background: COLORS.surface,
        borderRadius: 8,
        padding: 16,
        border: `1px solid ${COLORS.line}`,
        display: "flex",
        flexDirection: "column",
        transition: "transform 150ms ease-out, box-shadow 150ms ease-out",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.06)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* Top row: difficulty pill + id */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <span
          style={{
            padding: "5px 12px",
            borderRadius: 999,
            border: `1px solid ${meta.color}`,
            background: meta.soft,
            color: meta.color,
            fontSize: 14,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{ fontFamily: "system-ui", fontSize: 11, letterSpacing: 1.5 }}
          >
            {meta.dots}
          </span>
          {d} · {meta.sub}
        </span>
        <span style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 14,
            color: COLORS.muted,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          No.{String(s.id).padStart(3, "0")}
        </span>
      </div>

      {/* Patient — no name shown (avoid bias) */}
      <div
        style={{
          fontSize: 15,
          color: COLORS.muted,
          marginBottom: 12,
        }}
      >
        {s.gender}・{ageRangeLabel(s.ageRange)}
      </div>

      {s.guidanceLabel && (
        <div
          style={{
            alignSelf: "flex-start",
            padding: "4px 9px",
            marginBottom: 12,
            borderRadius: 999,
            border: "1px solid #0E7490",
            background: "#ECFEFF",
            color: "#0E7490",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          課題文: {s.guidanceLabel} · {s.tasks.length}タスク
        </div>
      )}

      {/* Summary */}
      <div
        style={{
          fontSize: 16,
          color: COLORS.ink,
          fontWeight: 700,
          lineHeight: 1.6,
          marginBottom: 14,
          flex: 1,
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
        }}
      >
        {summaryOf(s)}
      </div>

      {/* Theme tags */}
      {s.targetCategories.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 4,
            flexWrap: "wrap",
            marginBottom: 14,
          }}
        >
          {visibleScenarioCategories(s).map((c) => (
            <span
              key={c}
              style={{
                fontSize: 13,
                padding: "3px 10px",
                borderRadius: 6,
                background: COLORS.bg,
                color: COLORS.muted,
                fontWeight: 600,
              }}
            >
              {shortThemeLabel(themeLabel(c, s.targetCategoryLabels))}
            </span>
          ))}
          {remainingScenarioCategoryCount(s) > 0 && (
            <span
              style={{
                fontSize: 13,
                padding: "3px 10px",
                borderRadius: 6,
                background: COLORS.bg,
                color: COLORS.muted,
                fontWeight: 600,
              }}
            >
              ほか{remainingScenarioCategoryCount(s)}項目
            </span>
          )}
        </div>
      )}

      {/* The preview page contains the full task and category list. */}
      <button
        type="button"
        onClick={onStart}
        style={{
          width: "100%",
          padding: "12px",
          borderRadius: 8,
          background: COLORS.ink,
          color: COLORS.surface,
          border: "none",
          fontSize: 15,
          fontWeight: 700,
          cursor: "pointer",
          fontFamily: "inherit",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
        }}
      >
        内容を見る
        <ChevronRight size={17} aria-hidden />
      </button>
    </div>
  );
}

function MobileScenarioCard({
  s,
  onStart,
}: {
  s: Scenario;
  onStart: () => void;
}) {
  const d = difficultyOf(s);
  const meta = DIFFICULTY_META[d];
  return (
    <div
      style={{
        width: "100%",
        padding: 14,
        borderRadius: 8,
        border: `1px solid ${COLORS.line}`,
        background: COLORS.surface,
        color: COLORS.ink,
        fontFamily: "inherit",
        textAlign: "left",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <span
          style={{
            padding: "4px 9px",
            borderRadius: 999,
            border: `1px solid ${meta.color}`,
            background: meta.soft,
            color: meta.color,
            fontSize: 11,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: 6,
            lineHeight: 1,
          }}
        >
          <span style={{ fontFamily: "system-ui", fontSize: 9, letterSpacing: 1.5 }}>
            {meta.dots}
          </span>
          {d} · {meta.sub}
        </span>
        <span style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 11,
            color: COLORS.muted,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          No.{String(s.id).padStart(3, "0")}
        </span>
      </div>

      <div
        style={{
          fontSize: 11.5,
          color: COLORS.muted,
          marginBottom: 10,
        }}
      >
        {s.gender}・{ageRangeLabel(s.ageRange)}
      </div>

      {s.guidanceLabel && (
        <div
          style={{
            display: "inline-flex",
            padding: "4px 9px",
            marginBottom: 10,
            borderRadius: 999,
            border: "1px solid #0E7490",
            background: "#ECFEFF",
            color: "#0E7490",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          課題文: {s.guidanceLabel} · {s.tasks.length}タスク
        </div>
      )}

      <div
        style={{
          fontSize: 13,
          color: COLORS.ink,
          lineHeight: 1.55,
          marginBottom: 12,
          fontWeight: 700,
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
        }}
      >
        {summaryOf(s)}
      </div>

      {s.targetCategories.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 4,
            flexWrap: "wrap",
            marginBottom: 12,
          }}
        >
          {visibleScenarioCategories(s).map((c) => (
            <span
              key={c}
              style={{
                fontSize: 10.5,
                padding: "2px 8px",
                borderRadius: 6,
                background: COLORS.bg,
                color: COLORS.muted,
                fontWeight: 600,
                lineHeight: 1.55,
              }}
            >
              {shortThemeLabel(themeLabel(c, s.targetCategoryLabels))}
            </span>
          ))}
          {remainingScenarioCategoryCount(s) > 0 && (
            <span
              style={{
                fontSize: 10.5,
                padding: "2px 8px",
                borderRadius: 6,
                background: COLORS.bg,
                color: COLORS.muted,
                fontWeight: 600,
                lineHeight: 1.55,
              }}
            >
              ほか{remainingScenarioCategoryCount(s)}項目
            </span>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={onStart}
        style={{
          width: "100%",
          padding: "10px",
          borderRadius: 8,
          background: COLORS.ink,
          color: COLORS.surface,
          border: "none",
          fontSize: 12.5,
          fontWeight: 700,
          cursor: "pointer",
          fontFamily: "inherit",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
        }}
      >
        内容を見る
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

function DiceIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <circle cx="8.5" cy="8.5" r="1.2" fill="currentColor" />
      <circle cx="15.5" cy="8.5" r="1.2" fill="currentColor" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" />
      <circle cx="8.5" cy="15.5" r="1.2" fill="currentColor" />
      <circle cx="15.5" cy="15.5" r="1.2" fill="currentColor" />
    </svg>
  );
}
